import type {Configuration} from '../configuration';
import type {AxiosInstance, AxiosPromise, RawAxiosRequestConfig} from 'axios';
import {BASE_PATH, BaseAPI, operationServerMap, RequestArgs} from '../base';
import globalAxios from 'axios';
import {
  assertParamExists,
  createRequestFunction,
  DUMMY_BASE_URL,
  serializeDataIfNeeded,
  setSearchParams,
  toPathString,
} from '../common';
import {
  User,
  UserUpdateReq,
  Users,
  SendInviteReq,
  UpdatePasswordReq,
  UserRequestOrgDeletionReq,
  UserRequestOrgDeletionResponse,
  UserConfirmOrgDeletionReq,
  UserConfirmOrgDeletionResponse,
} from '../types/user';

/**
 * UserServiceApi - axios parameter creator
 * @export
 */
export const UserServiceApiAxiosParamCreator = function (configuration?: Configuration) {
  return {
    /**
     * 用户删除
     * @summary 用户删除
     * @param {string} id
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    userServiceDelete: async (
      id: string,
      options: RawAxiosRequestConfig = {}
    ): Promise<RequestArgs> => {
      // verify required parameter 'id' is not null or undefined
      assertParamExists('userServiceDelete', 'id', id);
      const localVarPath = `/user/user/{id}`.replace(`{${'id'}}`, encodeURIComponent(String(id)));
      // use dummy base URL string because the URL constructor only accepts absolute URLs.
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }

      const localVarRequestOptions = {method: 'DELETE', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;

      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers,
      };

      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions,
      };
    },
    /**
     * 用户详情
     * @summary 用户详情
     * @param {string} id
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    userServiceDetail: async (
      id: string,
      options: RawAxiosRequestConfig = {}
    ): Promise<RequestArgs> => {
      // verify required parameter 'id' is not null or undefined
      assertParamExists('userServiceDetail', 'id', id);
      const localVarPath = `/user/user/{id}`.replace(`{${'id'}}`, encodeURIComponent(String(id)));
      // use dummy base URL string because the URL constructor only accepts absolute URLs.
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }

      const localVarRequestOptions = {method: 'GET', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;

      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers,
      };

      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions,
      };
    },
    /**
     * 用户列表查询
     * @summary 用户列表查询
     * @param {string} [orgId]
     * @param {number} [pageSize] 当前分页大小
     * @param {number} [pageNum] 当前页数
     * @param {UserServiceFindNameOrderEnum} [nameOrder] sort order
     * @param {string} [nameEq] full match query equal
     * @param {string} [nameMatch] fuzzy search
     * @param {UserServiceFindEmailOrderEnum} [emailOrder] sort order
     * @param {string} [emailEq] full match query equal
     * @param {string} [emailMatch] fuzzy search
     * @param {UserServiceFindUpdatedAtOrderEnum} [updatedAtOrder] sort order
     * @param {string} [updatedAtEq] full match query equal
     * @param {string} [updatedAtMatch] fuzzy search
     * @param {UserServiceFindLanguageOrderEnum} [languageOrder] sort order
     * @param {string} [languageEq] full match query equal
     * @param {string} [languageMatch] fuzzy search
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    userServiceFind: async (
      orgId?: string,
      pageSize?: number,
      pageNum?: number,
      nameOrder?: UserServiceFindNameOrderEnum,
      nameEq?: string,
      nameMatch?: string,
      emailOrder?: UserServiceFindEmailOrderEnum,
      emailEq?: string,
      emailMatch?: string,
      updatedAtOrder?: UserServiceFindUpdatedAtOrderEnum,
      updatedAtEq?: string,
      updatedAtMatch?: string,
      languageOrder?: UserServiceFindLanguageOrderEnum,
      languageEq?: string,
      languageMatch?: string,
      options: RawAxiosRequestConfig = {}
    ): Promise<RequestArgs> => {
      const localVarPath = `/user/users`;
      // use dummy base URL string because the URL constructor only accepts absolute URLs.
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }

      const localVarRequestOptions = {method: 'GET', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;

      if (orgId !== undefined) {
        localVarQueryParameter['orgId'] = orgId;
      }

      if (pageSize !== undefined) {
        localVarQueryParameter['page.size'] = pageSize;
      }

      if (pageNum !== undefined) {
        localVarQueryParameter['page.num'] = pageNum;
      }

      if (nameOrder !== undefined) {
        localVarQueryParameter['name.order'] = nameOrder;
      }

      if (nameEq !== undefined) {
        localVarQueryParameter['name.eq'] = nameEq;
      }

      if (nameMatch !== undefined) {
        localVarQueryParameter['name.match'] = nameMatch;
      }

      if (emailOrder !== undefined) {
        localVarQueryParameter['email.order'] = emailOrder;
      }

      if (emailEq !== undefined) {
        localVarQueryParameter['email.eq'] = emailEq;
      }

      if (emailMatch !== undefined) {
        localVarQueryParameter['email.match'] = emailMatch;
      }

      if (updatedAtOrder !== undefined) {
        localVarQueryParameter['updatedAt.order'] = updatedAtOrder;
      }

      if (updatedAtEq !== undefined) {
        localVarQueryParameter['updatedAt.eq'] = updatedAtEq;
      }

      if (updatedAtMatch !== undefined) {
        localVarQueryParameter['updatedAt.match'] = updatedAtMatch;
      }

      if (languageOrder !== undefined) {
        localVarQueryParameter['language.order'] = languageOrder;
      }

      if (languageEq !== undefined) {
        localVarQueryParameter['language.eq'] = languageEq;
      }

      if (languageMatch !== undefined) {
        localVarQueryParameter['language.match'] = languageMatch;
      }

      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers,
      };

      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions,
      };
    },
    /**
     * 用户邀请注册   邀请外部的注册用户, 比如租客, 公寓管理员
     * @summary 用户邀请注册   邀请外部的注册用户, 比如租客, 公寓管理员
     * @param {string} inviteUserId 邀请者 id
     * @param {SendInviteReq} sendInviteReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    userServiceSendInvite: async (
      inviteUserId: string,
      sendInviteReq: SendInviteReq,
      options: RawAxiosRequestConfig = {}
    ): Promise<RequestArgs> => {
      // verify required parameter 'inviteUserId' is not null or undefined
      assertParamExists('userServiceSendInvite', 'inviteUserId', inviteUserId);
      // verify required parameter 'sendInviteReq' is not null or undefined
      assertParamExists('userServiceSendInvite', 'sendInviteReq', sendInviteReq);
      const localVarPath = `/user/user/{inviteUserId}/invite_email`.replace(
        `{${'inviteUserId'}}`,
        encodeURIComponent(String(inviteUserId))
      );
      // use dummy base URL string because the URL constructor only accepts absolute URLs.
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }

      const localVarRequestOptions = {method: 'POST', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;

      localVarHeaderParameter['Content-Type'] = 'application/json';

      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers,
      };
      localVarRequestOptions.data = serializeDataIfNeeded(
        sendInviteReq,
        localVarRequestOptions,
        configuration
      );

      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions,
      };
    },
    /**
     * 用户信息修改
     * @summary 用户信息修改
     * @param {string} id
     * @param {UserUpdateReq} userUpdateReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    userServiceUpdate: async (
      id: string,
      userUpdateReq: UserUpdateReq,
      options: RawAxiosRequestConfig = {}
    ): Promise<RequestArgs> => {
      // verify required parameter 'id' is not null or undefined
      assertParamExists('userServiceUpdate', 'id', id);
      // verify required parameter 'userUpdateReq' is not null or undefined
      assertParamExists('userServiceUpdate', 'userUpdateReq', userUpdateReq);
      const localVarPath = `/user/user/{id}`.replace(`{${'id'}}`, encodeURIComponent(String(id)));
      // use dummy base URL string because the URL constructor only accepts absolute URLs.
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }

      const localVarRequestOptions = {method: 'PUT', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;

      localVarHeaderParameter['Content-Type'] = 'application/json';

      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers,
      };
      localVarRequestOptions.data = serializeDataIfNeeded(
        userUpdateReq,
        localVarRequestOptions,
        configuration
      );

      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions,
      };
    },
    /**
     * 用户密码修改   修改密码需要用户的 token
     * @summary 用户密码修改   修改密码需要用户的 token
     * @param {string} userId
     * @param {UpdatePasswordReq} updatePasswordReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    userServiceUpdatePassword: async (
      userId: string,
      updatePasswordReq: UpdatePasswordReq,
      options: RawAxiosRequestConfig = {}
    ): Promise<RequestArgs> => {
      // verify required parameter 'userId' is not null or undefined
      assertParamExists('userServiceUpdatePassword', 'userId', userId);
      // verify required parameter 'updatePasswordReq' is not null or undefined
      assertParamExists('userServiceUpdatePassword', 'updatePasswordReq', updatePasswordReq);
      const localVarPath = `/user/user/{userId}/password`.replace(
        `{${'userId'}}`,
        encodeURIComponent(String(userId))
      );
      // use dummy base URL string because the URL constructor only accepts absolute URLs.
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }

      const localVarRequestOptions = {method: 'PATCH', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;

      localVarHeaderParameter['Content-Type'] = 'application/json';

      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers,
      };
      localVarRequestOptions.data = serializeDataIfNeeded(
        updatePasswordReq,
        localVarRequestOptions,
        configuration
      );

      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions,
      };
    },
    /**
     * 请求删除组织   组织拥有者请求删除组织，系统会发送验证邮件
     * @summary 请求删除组织   组织拥有者请求删除组织，系统会发送验证邮件
     * @param {UserRequestOrgDeletionReq} userRequestOrgDeletionReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    userServiceRequestOrgDeletion: async (
      userRequestOrgDeletionReq: UserRequestOrgDeletionReq,
      options: RawAxiosRequestConfig = {}
    ): Promise<RequestArgs> => {
      // verify required parameter 'userRequestOrgDeletionReq' is not null or undefined
      assertParamExists(
        'userServiceRequestOrgDeletion',
        'userRequestOrgDeletionReq',
        userRequestOrgDeletionReq
      );
      const localVarPath = `/user/request-org-deletion`;
      // use dummy base URL string because the URL constructor only accepts absolute URLs.
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }

      const localVarRequestOptions = {method: 'POST', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;

      localVarHeaderParameter['Content-Type'] = 'application/json';

      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers,
      };
      localVarRequestOptions.data = serializeDataIfNeeded(
        userRequestOrgDeletionReq,
        localVarRequestOptions,
        configuration
      );

      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions,
      };
    },
    /**
     * 确认删除组织   使用邮件中的验证码确认删除组织
     * @summary 确认删除组织   使用邮件中的验证码确认删除组织
     * @param {UserConfirmOrgDeletionReq} userConfirmOrgDeletionReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    userServiceConfirmOrgDeletion: async (
      userConfirmOrgDeletionReq: UserConfirmOrgDeletionReq,
      options: RawAxiosRequestConfig = {}
    ): Promise<RequestArgs> => {
      // verify required parameter 'userConfirmOrgDeletionReq' is not null or undefined
      assertParamExists(
        'userServiceConfirmOrgDeletion',
        'userConfirmOrgDeletionReq',
        userConfirmOrgDeletionReq
      );
      const localVarPath = `/user/confirm-org-deletion`;
      // use dummy base URL string because the URL constructor only accepts absolute URLs.
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }

      const localVarRequestOptions = {method: 'POST', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;

      localVarHeaderParameter['Content-Type'] = 'application/json';

      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers,
      };
      localVarRequestOptions.data = serializeDataIfNeeded(
        userConfirmOrgDeletionReq,
        localVarRequestOptions,
        configuration
      );

      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions,
      };
    },
  };
};

/**
 * UserServiceApi - functional programming interface
 * @export
 */
export const UserServiceApiFp = function (configuration?: Configuration) {
  const localVarAxiosParamCreator = UserServiceApiAxiosParamCreator(configuration);
  return {
    /**
     * 用户删除
     * @summary 用户删除
     * @param {string} id
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    async userServiceDelete(
      id: string,
      options?: RawAxiosRequestConfig
    ): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
      const localVarAxiosArgs = await localVarAxiosParamCreator.userServiceDelete(id, options);
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['UserServiceApi.userServiceDelete']?.[localVarOperationServerIndex]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },
    /**
     * 用户详情
     * @summary 用户详情
     * @param {string} id
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    async userServiceDetail(
      id: string,
      options?: RawAxiosRequestConfig
    ): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<User>> {
      const localVarAxiosArgs = await localVarAxiosParamCreator.userServiceDetail(id, options);
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['UserServiceApi.userServiceDetail']?.[localVarOperationServerIndex]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },
    /**
     * 用户列表查询
     * @summary 用户列表查询
     * @param {string} [orgId]
     * @param {number} [pageSize] 当前分页大小
     * @param {number} [pageNum] 当前页数
     * @param {UserServiceFindNameOrderEnum} [nameOrder] sort order
     * @param {string} [nameEq] full match query equal
     * @param {string} [nameMatch] fuzzy search
     * @param {UserServiceFindEmailOrderEnum} [emailOrder] sort order
     * @param {string} [emailEq] full match query equal
     * @param {string} [emailMatch] fuzzy search
     * @param {UserServiceFindUpdatedAtOrderEnum} [updatedAtOrder] sort order
     * @param {string} [updatedAtEq] full match query equal
     * @param {string} [updatedAtMatch] fuzzy search
     * @param {UserServiceFindLanguageOrderEnum} [languageOrder] sort order
     * @param {string} [languageEq] full match query equal
     * @param {string} [languageMatch] fuzzy search
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    async userServiceFind(
      orgId?: string,
      pageSize?: number,
      pageNum?: number,
      nameOrder?: UserServiceFindNameOrderEnum,
      nameEq?: string,
      nameMatch?: string,
      emailOrder?: UserServiceFindEmailOrderEnum,
      emailEq?: string,
      emailMatch?: string,
      updatedAtOrder?: UserServiceFindUpdatedAtOrderEnum,
      updatedAtEq?: string,
      updatedAtMatch?: string,
      languageOrder?: UserServiceFindLanguageOrderEnum,
      languageEq?: string,
      languageMatch?: string,
      options?: RawAxiosRequestConfig
    ): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Users>> {
      const localVarAxiosArgs = await localVarAxiosParamCreator.userServiceFind(
        orgId,
        pageSize,
        pageNum,
        nameOrder,
        nameEq,
        nameMatch,
        emailOrder,
        emailEq,
        emailMatch,
        updatedAtOrder,
        updatedAtEq,
        updatedAtMatch,
        languageOrder,
        languageEq,
        languageMatch,
        options
      );
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['UserServiceApi.userServiceFind']?.[localVarOperationServerIndex]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },
    /**
     * 用户邀请注册   邀请外部的注册用户, 比如租客, 公寓管理员
     * @summary 用户邀请注册   邀请外部的注册用户, 比如租客, 公寓管理员
     * @param {string} inviteUserId 邀请者 id
     * @param {SendInviteReq} sendInviteReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    async userServiceSendInvite(
      inviteUserId: string,
      sendInviteReq: SendInviteReq,
      options?: RawAxiosRequestConfig
    ): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
      const localVarAxiosArgs = await localVarAxiosParamCreator.userServiceSendInvite(
        inviteUserId,
        sendInviteReq,
        options
      );
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['UserServiceApi.userServiceSendInvite']?.[localVarOperationServerIndex]
          ?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },
    /**
     * 用户信息修改
     * @summary 用户信息修改
     * @param {string} id
     * @param {UserUpdateReq} userUpdateReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    async userServiceUpdate(
      id: string,
      userUpdateReq: UserUpdateReq,
      options?: RawAxiosRequestConfig
    ): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
      const localVarAxiosArgs = await localVarAxiosParamCreator.userServiceUpdate(
        id,
        userUpdateReq,
        options
      );
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['UserServiceApi.userServiceUpdate']?.[localVarOperationServerIndex]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },
    /**
     * 用户密码修改   修改密码需要用户的 token
     * @summary 用户密码修改   修改密码需要用户的 token
     * @param {string} userId
     * @param {UpdatePasswordReq} updatePasswordReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    async userServiceUpdatePassword(
      userId: string,
      updatePasswordReq: UpdatePasswordReq,
      options?: RawAxiosRequestConfig
    ): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
      const localVarAxiosArgs = await localVarAxiosParamCreator.userServiceUpdatePassword(
        userId,
        updatePasswordReq,
        options
      );
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['UserServiceApi.userServiceUpdatePassword']?.[
          localVarOperationServerIndex
        ]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },
    /**
     * 请求删除组织   组织拥有者请求删除组织，系统会发送验证邮件
     * @summary 请求删除组织   组织拥有者请求删除组织，系统会发送验证邮件
     * @param {UserRequestOrgDeletionReq} userRequestOrgDeletionReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    async userServiceRequestOrgDeletion(
      userRequestOrgDeletionReq: UserRequestOrgDeletionReq,
      options?: RawAxiosRequestConfig
    ): Promise<
      (axios?: AxiosInstance, basePath?: string) => AxiosPromise<UserRequestOrgDeletionResponse>
    > {
      const localVarAxiosArgs = await localVarAxiosParamCreator.userServiceRequestOrgDeletion(
        userRequestOrgDeletionReq,
        options
      );
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['UserServiceApi.userServiceRequestOrgDeletion']?.[
          localVarOperationServerIndex
        ]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },
    /**
     * 确认删除组织   使用邮件中的验证码确认删除组织
     * @summary 确认删除组织   使用邮件中的验证码确认删除组织
     * @param {UserConfirmOrgDeletionReq} userConfirmOrgDeletionReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    async userServiceConfirmOrgDeletion(
      userConfirmOrgDeletionReq: UserConfirmOrgDeletionReq,
      options?: RawAxiosRequestConfig
    ): Promise<
      (axios?: AxiosInstance, basePath?: string) => AxiosPromise<UserConfirmOrgDeletionResponse>
    > {
      const localVarAxiosArgs = await localVarAxiosParamCreator.userServiceConfirmOrgDeletion(
        userConfirmOrgDeletionReq,
        options
      );
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['UserServiceApi.userServiceConfirmOrgDeletion']?.[
          localVarOperationServerIndex
        ]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },
  };
};

/**
 * UserServiceApi - factory interface
 * @export
 */
export const UserServiceApiFactory = function (
  configuration?: Configuration,
  basePath?: string,
  axios?: AxiosInstance
) {
  const localVarFp = UserServiceApiFp(configuration);
  return {
    /**
     * 用户删除
     * @summary 用户删除
     * @param {string} id
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    userServiceDelete(id: string, options?: any): AxiosPromise<void> {
      return localVarFp.userServiceDelete(id, options).then(request => request(axios, basePath));
    },
    /**
     * 用户详情
     * @summary 用户详情
     * @param {string} id
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    userServiceDetail(id: string, options?: any): AxiosPromise<User> {
      return localVarFp.userServiceDetail(id, options).then(request => request(axios, basePath));
    },
    /**
     * 用户列表查询
     * @summary 用户列表查询
     * @param {string} [orgId]
     * @param {number} [pageSize] 当前分页大小
     * @param {number} [pageNum] 当前页数
     * @param {UserServiceFindNameOrderEnum} [nameOrder] sort order
     * @param {string} [nameEq] full match query equal
     * @param {string} [nameMatch] fuzzy search
     * @param {UserServiceFindEmailOrderEnum} [emailOrder] sort order
     * @param {string} [emailEq] full match query equal
     * @param {string} [emailMatch] fuzzy search
     * @param {UserServiceFindUpdatedAtOrderEnum} [updatedAtOrder] sort order
     * @param {string} [updatedAtEq] full match query equal
     * @param {string} [updatedAtMatch] fuzzy search
     * @param {UserServiceFindLanguageOrderEnum} [languageOrder] sort order
     * @param {string} [languageEq] full match query equal
     * @param {string} [languageMatch] fuzzy search
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    userServiceFind(
      orgId?: string,
      pageSize?: number,
      pageNum?: number,
      nameOrder?: UserServiceFindNameOrderEnum,
      nameEq?: string,
      nameMatch?: string,
      emailOrder?: UserServiceFindEmailOrderEnum,
      emailEq?: string,
      emailMatch?: string,
      updatedAtOrder?: UserServiceFindUpdatedAtOrderEnum,
      updatedAtEq?: string,
      updatedAtMatch?: string,
      languageOrder?: UserServiceFindLanguageOrderEnum,
      languageEq?: string,
      languageMatch?: string,
      options?: any
    ): AxiosPromise<Users> {
      return localVarFp
        .userServiceFind(
          orgId,
          pageSize,
          pageNum,
          nameOrder,
          nameEq,
          nameMatch,
          emailOrder,
          emailEq,
          emailMatch,
          updatedAtOrder,
          updatedAtEq,
          updatedAtMatch,
          languageOrder,
          languageEq,
          languageMatch,
          options
        )
        .then(request => request(axios, basePath));
    },
    /**
     * 用户邀请注册   邀请外部的注册用户, 比如租客, 公寓管理员
     * @summary 用户邀请注册   邀请外部的注册用户, 比如租客, 公寓管理员
     * @param {string} inviteUserId 邀请者 id
     * @param {SendInviteReq} sendInviteReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    userServiceSendInvite(
      inviteUserId: string,
      sendInviteReq: SendInviteReq,
      options?: any
    ): AxiosPromise<void> {
      return localVarFp
        .userServiceSendInvite(inviteUserId, sendInviteReq, options)
        .then(request => request(axios, basePath));
    },
    /**
     * 用户信息修改
     * @summary 用户信息修改
     * @param {string} id
     * @param {UserUpdateReq} userUpdateReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    userServiceUpdate(id: string, userUpdateReq: UserUpdateReq, options?: any): AxiosPromise<void> {
      return localVarFp
        .userServiceUpdate(id, userUpdateReq, options)
        .then(request => request(axios, basePath));
    },
    /**
     * 用户密码修改   修改密码需要用户的 token
     * @summary 用户密码修改   修改密码需要用户的 token
     * @param {string} userId
     * @param {UpdatePasswordReq} updatePasswordReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    userServiceUpdatePassword(
      userId: string,
      updatePasswordReq: UpdatePasswordReq,
      options?: any
    ): AxiosPromise<void> {
      return localVarFp
        .userServiceUpdatePassword(userId, updatePasswordReq, options)
        .then(request => request(axios, basePath));
    },
    /**
     * 请求删除组织   组织拥有者请求删除组织，系统会发送验证邮件
     * @summary 请求删除组织   组织拥有者请求删除组织，系统会发送验证邮件
     * @param {UserRequestOrgDeletionReq} userRequestOrgDeletionReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    userServiceRequestOrgDeletion(
      userRequestOrgDeletionReq: UserRequestOrgDeletionReq,
      options?: any
    ): AxiosPromise<UserRequestOrgDeletionResponse> {
      return localVarFp
        .userServiceRequestOrgDeletion(userRequestOrgDeletionReq, options)
        .then(request => request(axios, basePath));
    },
    /**
     * 确认删除组织   使用邮件中的验证码确认删除组织
     * @summary 确认删除组织   使用邮件中的验证码确认删除组织
     * @param {UserConfirmOrgDeletionReq} userConfirmOrgDeletionReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    userServiceConfirmOrgDeletion(
      userConfirmOrgDeletionReq: UserConfirmOrgDeletionReq,
      options?: any
    ): AxiosPromise<UserConfirmOrgDeletionResponse> {
      return localVarFp
        .userServiceConfirmOrgDeletion(userConfirmOrgDeletionReq, options)
        .then(request => request(axios, basePath));
    },
  };
};

/**
 * UserServiceApi - object-oriented interface
 * @export
 * @class UserServiceApi
 * @extends {BaseAPI}
 */
export class UserServiceApi extends BaseAPI {
  /**
   * 用户删除
   * @summary 用户删除
   * @param {string} id
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof UserServiceApi
   */
  public userServiceDelete(id: string, options?: RawAxiosRequestConfig) {
    return UserServiceApiFp(this.configuration)
      .userServiceDelete(id, options)
      .then(request => request(this.axios, this.basePath));
  }

  /**
   * 用户详情
   * @summary 用户详情
   * @param {string} id
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof UserServiceApi
   */
  public userServiceDetail(id: string, options?: RawAxiosRequestConfig) {
    return UserServiceApiFp(this.configuration)
      .userServiceDetail(id, options)
      .then(request => request(this.axios, this.basePath));
  }

  /**
   * 用户列表查询
   * @summary 用户列表查询
   * @param {string} [orgId]
   * @param {number} [pageSize] 当前分页大小
   * @param {number} [pageNum] 当前页数
   * @param {UserServiceFindNameOrderEnum} [nameOrder] sort order
   * @param {string} [nameEq] full match query equal
   * @param {string} [nameMatch] fuzzy search
   * @param {UserServiceFindEmailOrderEnum} [emailOrder] sort order
   * @param {string} [emailEq] full match query equal
   * @param {string} [emailMatch] fuzzy search
   * @param {UserServiceFindUpdatedAtOrderEnum} [updatedAtOrder] sort order
   * @param {string} [updatedAtEq] full match query equal
   * @param {string} [updatedAtMatch] fuzzy search
   * @param {UserServiceFindLanguageOrderEnum} [languageOrder] sort order
   * @param {string} [languageEq] full match query equal
   * @param {string} [languageMatch] fuzzy search
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof UserServiceApi
   */
  public userServiceFind(
    orgId?: string,
    pageSize?: number,
    pageNum?: number,
    nameOrder?: UserServiceFindNameOrderEnum,
    nameEq?: string,
    nameMatch?: string,
    emailOrder?: UserServiceFindEmailOrderEnum,
    emailEq?: string,
    emailMatch?: string,
    updatedAtOrder?: UserServiceFindUpdatedAtOrderEnum,
    updatedAtEq?: string,
    updatedAtMatch?: string,
    languageOrder?: UserServiceFindLanguageOrderEnum,
    languageEq?: string,
    languageMatch?: string,
    options?: RawAxiosRequestConfig
  ) {
    return UserServiceApiFp(this.configuration)
      .userServiceFind(
        orgId,
        pageSize,
        pageNum,
        nameOrder,
        nameEq,
        nameMatch,
        emailOrder,
        emailEq,
        emailMatch,
        updatedAtOrder,
        updatedAtEq,
        updatedAtMatch,
        languageOrder,
        languageEq,
        languageMatch,
        options
      )
      .then(request => request(this.axios, this.basePath));
  }

  /**
   * 用户邀请注册   邀请外部的注册用户, 比如租客, 公寓管理员
   * @summary 用户邀请注册   邀请外部的注册用户, 比如租客, 公寓管理员
   * @param {string} inviteUserId 邀请者 id
   * @param {SendInviteReq} sendInviteReq
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof UserServiceApi
   */
  public userServiceSendInvite(
    inviteUserId: string,
    sendInviteReq: SendInviteReq,
    options?: RawAxiosRequestConfig
  ) {
    return UserServiceApiFp(this.configuration)
      .userServiceSendInvite(inviteUserId, sendInviteReq, options)
      .then(request => request(this.axios, this.basePath));
  }

  /**
   * 用户信息修改
   * @summary 用户信息修改
   * @param {string} id
   * @param {UserUpdateReq} userUpdateReq
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof UserServiceApi
   */
  public userServiceUpdate(
    id: string,
    userUpdateReq: UserUpdateReq,
    options?: RawAxiosRequestConfig
  ) {
    return UserServiceApiFp(this.configuration)
      .userServiceUpdate(id, userUpdateReq, options)
      .then(request => request(this.axios, this.basePath));
  }

  /**
   * 用户密码修改   修改密码需要用户的 token
   * @summary 用户密码修改   修改密码需要用户的 token
   * @param {string} userId
   * @param {UpdatePasswordReq} updatePasswordReq
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof UserServiceApi
   */
  public userServiceUpdatePassword(
    userId: string,
    updatePasswordReq: UpdatePasswordReq,
    options?: RawAxiosRequestConfig
  ) {
    return UserServiceApiFp(this.configuration)
      .userServiceUpdatePassword(userId, updatePasswordReq, options)
      .then(request => request(this.axios, this.basePath));
  }

  /**
   * 请求删除组织   组织拥有者请求删除组织，系统会发送验证邮件
   * @summary 请求删除组织   组织拥有者请求删除组织，系统会发送验证邮件
   * @param {UserRequestOrgDeletionReq} userRequestOrgDeletionReq
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof UserServiceApi
   */
  public userServiceRequestOrgDeletion(
    userRequestOrgDeletionReq: UserRequestOrgDeletionReq,
    options?: RawAxiosRequestConfig
  ) {
    return UserServiceApiFp(this.configuration)
      .userServiceRequestOrgDeletion(userRequestOrgDeletionReq, options)
      .then(request => request(this.axios, this.basePath));
  }

  /**
   * 确认删除组织   使用邮件中的验证码确认删除组织
   * @summary 确认删除组织   使用邮件中的验证码确认删除组织
   * @param {UserConfirmOrgDeletionReq} userConfirmOrgDeletionReq
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof UserServiceApi
   */
  public userServiceConfirmOrgDeletion(
    userConfirmOrgDeletionReq: UserConfirmOrgDeletionReq,
    options?: RawAxiosRequestConfig
  ) {
    return UserServiceApiFp(this.configuration)
      .userServiceConfirmOrgDeletion(userConfirmOrgDeletionReq, options)
      .then(request => request(this.axios, this.basePath));
  }
}

/**
 * @export
 */
export const UserServiceFindNameOrderEnum = {
  Asc: 'ASC',
  Desc: 'DESC',
} as const;
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type UserServiceFindNameOrderEnum =
  (typeof UserServiceFindNameOrderEnum)[keyof typeof UserServiceFindNameOrderEnum];
/**
 * @export
 */
export const UserServiceFindEmailOrderEnum = {
  Asc: 'ASC',
  Desc: 'DESC',
} as const;
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type UserServiceFindEmailOrderEnum =
  (typeof UserServiceFindEmailOrderEnum)[keyof typeof UserServiceFindEmailOrderEnum];
/**
 * @export
 */
export const UserServiceFindUpdatedAtOrderEnum = {
  Asc: 'ASC',
  Desc: 'DESC',
} as const;
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type UserServiceFindUpdatedAtOrderEnum =
  (typeof UserServiceFindUpdatedAtOrderEnum)[keyof typeof UserServiceFindUpdatedAtOrderEnum];
/**
 * @export
 */
export const UserServiceFindLanguageOrderEnum = {
  Asc: 'ASC',
  Desc: 'DESC',
} as const;
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type UserServiceFindLanguageOrderEnum =
  (typeof UserServiceFindLanguageOrderEnum)[keyof typeof UserServiceFindLanguageOrderEnum];
